import { connect } from 'react-redux';
import type { GreekLine } from 'state/quotes/details/details.model';
import { QuoteType } from 'state/quotes/infos/infos.model';
import type { SolveResultFormat } from 'state/quotes/results/result.model';
import { getQuoteType } from 'state/quotes/selectors';
import { isQuoteTypeTradable } from 'state/quotes/utils';
import {
  getFixedAmountInPercent,
  getFormQuoteDetails,
  getFormQuoteFxSpot,
  getFormQuoteId,
  getFormQuoteResult,
  getFormQuoteYield,
  getQuoteCurrency,
  getQuotePriceInCurrency,
  getQuotePriceInPercent,
  getQuotePricerToBookingInvertedFlow,
  getQuoteRemunerationMode,
  getQuoteSolvingMode,
  getSidePanelTradeWayOrDefault,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import type { FlowWay } from '../cashFlowWayUtils';
import { getCashFlowWay } from '../cashFlowWayUtils';

export interface OwnProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;
}

export interface StateProps {
  quoteType: QuoteType | undefined;
  solveResult: number;
  solveResultFormat?: SolveResultFormat;
  solvingMode: string;
  pricedOn: string;
  isMultipleSolvingMode: boolean;
  isTradeable: boolean;
  flowWay: FlowWay;
  fxSpot: GreekLine | null;
  remunerationMode: string;
  fixedAmountInPercent: number;
  priceInPercent?: number;
  priceInCurrency?: number;
  currency?: string;
  quoteYield?: string;
}

export type QuotedState = StateProps & OwnProps;

const mapStateToProps = (state: AppState, { tabId, workspaceType }: OwnProps): StateProps => {
  const solvingMode = getQuoteSolvingMode(state, tabId);
  const remunerationMode = getQuoteRemunerationMode(state, tabId);
  const quoteResult = getFormQuoteResult(state, workspaceType, tabId);
  const quoteDetails = getFormQuoteDetails(state, workspaceType, tabId);
  const quoteId = getFormQuoteId(state, workspaceType, tabId);
  const quoteType = getQuoteType(state, quoteId);
  const fixedAmountInPercent = getFixedAmountInPercent(state, tabId);
  const tradeWay = getSidePanelTradeWayOrDefault(state, tabId);
  const priceInPercent = getQuotePriceInPercent(state, tabId);
  const priceInCurrency = getQuotePriceInCurrency(state, workspaceType, tabId);
  const currency = getQuoteCurrency(state, workspaceType, tabId);
  const quoteYield = getFormQuoteYield(state, workspaceType, tabId);

  const pricerToBookingInvertedFlow = getQuotePricerToBookingInvertedFlow(state, workspaceType, tabId);
  const cashFlowWay = getCashFlowWay(priceInPercent, remunerationMode, tradeWay, pricerToBookingInvertedFlow);

  if (quoteResult?.solveResult === undefined) {
    throw new Error('Quote should exist and have a price when status is QUOTED_INDICATIVE');
  }

  const fxSpot = getFormQuoteFxSpot(state, workspaceType, tabId);

  return {
    quoteType,
    isTradeable: isQuoteTypeTradable(quoteType),
    solveResult: quoteResult.solveResult,
    solveResultFormat: quoteResult.solveResultFormat,
    solvingMode,
    isMultipleSolvingMode: quoteDetails?.IsMultipleSolvingMode ?? false,
    flowWay: cashFlowWay,
    fxSpot,
    pricedOn: quoteResult.createdAt!,
    remunerationMode,
    fixedAmountInPercent,
    priceInPercent,
    priceInCurrency,
    currency,
    quoteYield,
  };
};

export const connectQuotedIndicative = connect(mapStateToProps);
